import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { CleanupFamilyTransfersButton } from "./CleanupFamilyTransfersButton";
import { backupRecordsToExcel } from "@/utils/countyResearchBackup";

interface ComalRecord {
  documentNumber: string;
  recordedDate: string;
  grantorName: string;
  partyRole: string;
  docType: string;
  beneficiaryName: string;
  legalDescription: string;
  instrumentRef?: string;
}

interface Props {
  county?: string;
  onRecordsSaved: () => void;
}

export function ComalCountyTextParser({ county = 'comal', onRecordsSaved }: Props) {
  const [textData, setTextData] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const { toast } = useToast();

  const handleClearAddresses = async () => {
    const countyLabel = county === 'guadalupe' ? 'Guadalupe County' : 'Comal County';
    if (!confirm(`This will clear ALL addresses for ${countyLabel} records so they can be re-extracted. Continue?`)) {
      return;
    }

    setIsClearing(true);
    try {
      const { data, error } = await supabase.functions.invoke('clear-comal-addresses', {
        body: { county }
      });
      
      if (error) throw error;

      const countyLabel = county === 'guadalupe' ? 'Guadalupe County' : 'Comal County';
      toast({
        title: "Addresses cleared",
        description: `Cleared addresses for ${data.recordsUpdated} ${countyLabel} records. You can now re-extract them with the corrected logic.`,
      });
      
      onRecordsSaved();
    } catch (error) {
      console.error('Error clearing addresses:', error);
      toast({
        title: "Error",
        description: "Failed to clear addresses. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsClearing(false);
    }
  };

  const parseComalData = (text: string): ComalRecord[] => {
    const lines = text.trim().split('\n');
    
    // Detect format by checking first few lines
    let hasTwoEmptyColumns = false;
    let hasMultipleRowsPerDoc = false;
    const docNumbers = new Set<string>();
    
    for (let i = 0; i < Math.min(10, lines.length); i++) {
      const parts = lines[i].split('\t');
      if (parts.length >= 7 && parts[0]?.trim()) {
        // Check for 2 empty columns (Comal formats)
        if (parts[5]?.trim() === '' && parts[6]?.trim() === '') {
          hasTwoEmptyColumns = true;
        }
        
        // Check if document numbers repeat (multiple rows per doc)
        const docNum = parts[0]?.trim();
        if (docNum && docNumbers.has(docNum)) {
          hasMultipleRowsPerDoc = true;
        }
        if (docNum) docNumbers.add(docNum);
      }
    }
    
    // COMAL 2025 FORMAT: Single row per doc, beneficiary in column 7
    if (hasTwoEmptyColumns && !hasMultipleRowsPerDoc) {
      const records: ComalRecord[] = [];
      for (const line of lines) {
        const parts = line.split('\t');
        if (parts.length >= 8) {
          const lineText = line.toLowerCase();
          if (lineText.includes('first reference')) continue;
          
          records.push({
            documentNumber: parts[0]?.trim() || '',
            recordedDate: parts[1]?.trim() || '',
            grantorName: parts[2]?.trim() || '',
            partyRole: parts[3]?.trim() || '',
            docType: parts[4]?.trim() || '',
            beneficiaryName: parts[7]?.trim() || '',
            legalDescription: parts[8]?.trim() || '',
          });
        }
      }
      return records;
    }
    
    // COMAL 2023 FORMAT: Multiple rows per doc, 2 empty columns, role-based
    if (hasTwoEmptyColumns && hasMultipleRowsPerDoc) {
      const documentMap = new Map<string, { 
        documentNumber: string;
        recordedDate: string;
        docType: string;
        legalDescription: string;
        grantors: string[];
        grantees: string[];
      }>();

      for (const line of lines) {
        const parts = line.split('\t');
        if (parts.length >= 8) {
          const lineText = line.toLowerCase();
          if (lineText.includes('first reference')) continue;
          
          const docNumber = parts[0]?.trim() || '';
          const recordedDate = parts[1]?.trim() || '';
          const partyName = parts[2]?.trim() || '';
          const partyRole = parts[3]?.trim() || '';
          const docType = parts[4]?.trim() || '';
          const otherParty = parts[7]?.trim() || '';
          const legalDescription = parts[8]?.trim() || '';
          
          if (!docNumber) continue;
          
          if (!documentMap.has(docNumber)) {
            documentMap.set(docNumber, {
              documentNumber: docNumber,
              recordedDate: recordedDate,
              docType: docType,
              legalDescription: legalDescription,
              grantors: [],
              grantees: []
            });
          }
          
          const doc = documentMap.get(docNumber)!;
          
          if (partyRole.toLowerCase().includes('grantor')) {
            if (partyName && !doc.grantors.includes(partyName)) {
              doc.grantors.push(partyName);
            }
          } else if (partyRole.toLowerCase().includes('grantee')) {
            if (partyName && !doc.grantees.includes(partyName)) {
              doc.grantees.push(partyName);
            }
          }
        }
      }

      const records: ComalRecord[] = Array.from(documentMap.values()).map(doc => ({
        documentNumber: doc.documentNumber,
        recordedDate: doc.recordedDate,
        grantorName: doc.grantors.join(' & '),
        partyRole: 'multiple',
        docType: doc.docType,
        beneficiaryName: doc.grantees.join(' & '),
        legalDescription: doc.legalDescription,
      }));

      return records;
    }
    
    // GUADALUPE FORMAT: Multiple rows per document, NO empty columns, role-based
    const documentMap = new Map<string, { 
      documentNumber: string;
      recordedDate: string;
      docType: string;
      legalDescription: string;
      grantors: string[];
      grantees: string[];
    }>();

    for (const line of lines) {
      const parts = line.split('\t');
      if (parts.length >= 7) {
        const lineText = line.toLowerCase();
        if (lineText.includes('first reference')) continue;
        
        const docNumber = parts[0]?.trim() || '';
        const recordedDate = parts[1]?.trim() || '';
        const partyName = parts[2]?.trim() || '';
        const partyRole = parts[3]?.trim() || '';
        const docType = parts[4]?.trim() || '';
        const legalDescription = parts[5]?.trim() || '';
        
        if (!docNumber) continue;
        
        if (!documentMap.has(docNumber)) {
          documentMap.set(docNumber, {
            documentNumber: docNumber,
            recordedDate: recordedDate,
            docType: docType,
            legalDescription: legalDescription,
            grantors: [],
            grantees: []
          });
        }
        
        const doc = documentMap.get(docNumber)!;
        
        if (partyRole.toLowerCase().includes('grantor')) {
          if (partyName && !doc.grantors.includes(partyName)) {
            doc.grantors.push(partyName);
          }
        } else if (partyRole.toLowerCase().includes('grantee')) {
          if (partyName && !doc.grantees.includes(partyName)) {
            doc.grantees.push(partyName);
          }
        }
      }
    }

    const records: ComalRecord[] = Array.from(documentMap.values()).map(doc => ({
      documentNumber: doc.documentNumber,
      recordedDate: doc.recordedDate,
      grantorName: doc.grantors.join(' & '),
      partyRole: 'multiple',
      docType: doc.docType,
      beneficiaryName: doc.grantees.join(' & '),
      legalDescription: doc.legalDescription,
    }));

    return records;
  };

  const handleProcess = async () => {
    if (!textData.trim()) {
      toast({
        title: "No data",
        description: "Please paste the deed records data",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);

    try {
      // Parse the pasted data
      const records = parseComalData(textData);
      
      if (records.length === 0) {
        toast({
          title: "No records found",
          description: "Could not parse any records from the input",
          variant: "destructive",
        });
        return;
      }

      // Deduplicate records by document number (keep first occurrence)
      const uniqueRecordsMap = new Map();
      records.forEach(record => {
        if (!uniqueRecordsMap.has(record.documentNumber)) {
          uniqueRecordsMap.set(record.documentNumber, record);
        }
      });
      const uniqueRecords = Array.from(uniqueRecordsMap.values());
      
      const duplicatesInPaste = records.length - uniqueRecords.length;
      if (duplicatesInPaste > 0) {
        console.log(`Removed ${duplicatesInPaste} duplicate documents from pasted data`);
      }

      console.log(`${uniqueRecords.length} unique records to filter for owner-financing`);

      // Format data for AI filtering - analyze ALL unique records first
      const deedText = uniqueRecords.map(r => 
        `Document: ${r.documentNumber}\n` +
        `Date: ${r.recordedDate}\n` +
        `Grantor: ${r.grantorName}\n` +
        `Beneficiary: ${r.beneficiaryName}\n` +
        `Legal: ${r.legalDescription}\n`
      ).join('\n---\n');

      // Call the filter-owner-finance edge function to identify owner-financed deals
      const { data: filterData, error: filterError } = await supabase.functions.invoke(
        'filter-owner-finance',
        {
          body: { deedText }
        }
      );

      if (filterError) throw filterError;

      const ownerFinancedDeals = filterData?.filteredDeals || [];
      const ownerFinancedDocNumbers = new Set(
        ownerFinancedDeals.map((deal: any) => deal.documentNumber)
      );
      
      console.log(`AI identified ${ownerFinancedDocNumbers.size} owner-financed deals (individual beneficiaries)`);
      console.log(`Skipping ${uniqueRecords.length - ownerFinancedDocNumbers.size} corporate/institutional lenders`);

      // Only keep records with individual beneficiaries (owner-financed)
      const ownerFinancedRecords = uniqueRecords.filter(r => 
        ownerFinancedDocNumbers.has(r.documentNumber)
      );

      if (ownerFinancedRecords.length === 0) {
        toast({
          title: "No owner-financed deals found",
          description: `Analyzed ${uniqueRecords.length} records - all had corporate or institutional lenders`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // Check for existing document numbers among owner-financed deals (including soft-deleted)
      console.log('Checking for duplicates among', ownerFinancedRecords.length, 'owner-financed records');
      
      const existingDocsQuery = await supabase
        .from('county_research')
        .select('document_number, deleted_at')
        .eq('county', county)
        .in('document_number', ownerFinancedRecords.map(r => r.documentNumber));

      if (existingDocsQuery.error) {
        console.error('Error checking duplicates:', existingDocsQuery.error);
      }

      const existingDocs = new Set(
        existingDocsQuery.data?.map(d => d.document_number) || []
      );

      console.log('Found', existingDocs.size, 'existing documents (including soft-deleted)');

      // Filter out ALL duplicates (active AND soft-deleted) from owner-financed records
      const newRecords = ownerFinancedRecords.filter(r => !existingDocs.has(r.documentNumber));

      console.log('Will save', newRecords.length, 'new owner-financed records');

      if (newRecords.length === 0) {
        toast({
          title: "No new records",
          description: `Found ${ownerFinancedRecords.length} owner-financed deals, but all already exist in database`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }
      
      // Prepare only owner-financed records for insertion (never restore deleted records)
      const recordsToInsert = newRecords.map(record => ({
        county: county,
        document_number: record.documentNumber,
        filing_date: record.recordedDate.split(' ')[0] || new Date().toISOString().split('T')[0],
        document_type: record.docType,
        grantor_name: record.grantorName,
        grantee_name: record.beneficiaryName,
        legal_description: record.legalDescription,
        is_potential_owner_finance: true,
        confidence_score: 75,
        detection_reasons: ['AI identified as potential owner finance'],
        status: 'new',
        raw_data: record as any
      }));

      const { error: insertError } = await supabase
        .from('county_research')
        .upsert(recordsToInsert, { 
          onConflict: 'document_number',
          ignoreDuplicates: false 
        });

      if (insertError) {
        console.error('Insert error:', insertError);
        throw insertError;
      }

      backupRecordsToExcel(recordsToInsert, county, 'comal_parser');

      const totalAnalyzed = uniqueRecords.length;
      const totalOwnerFinanced = ownerFinancedRecords.length;
      const totalCorporate = totalAnalyzed - totalOwnerFinanced;
      const totalSaved = newRecords.length;
      const totalExisting = ownerFinancedRecords.length - newRecords.length;
      
      toast({
        title: "Success",
        description: `Saved ${totalSaved} owner-financed deals!\n` +
                     `Total analyzed: ${totalAnalyzed}\n` +
                     `Owner-financed: ${totalOwnerFinanced} (saved: ${totalSaved}, already in DB: ${totalExisting})\n` +
                     `Corporate lenders: ${totalCorporate} (skipped)`,
      });

      setTextData("");
      onRecordsSaved();

    } catch (error) {
      console.error('Error processing Comal County data:', error);
      toast({
        title: "Error",
        description: "Failed to process the records. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const countyLabel = county === 'guadalupe' ? 'Guadalupe County' : 'Comal County';
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>{countyLabel}, TX - Text Parser with AI Filtering (ROAM System)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Paste the deed of trust records from {countyLabel}. The AI will automatically identify potential owner-financed deals.
          </p>
          <p className="text-sm text-muted-foreground font-medium">
            <strong>3 Formats Supported:</strong>
          </p>
          <p className="text-sm text-muted-foreground">
            <strong>Comal 2025:</strong> Doc# [TAB] Date [TAB] Grantor [TAB] Role [TAB] DocType [TAB] [TAB] [TAB] Beneficiary [TAB] Legal
          </p>
          <p className="text-sm text-muted-foreground">
            <strong>Comal 2023:</strong> Multiple rows per doc, 2 empty columns, uses Grantor/Grantee roles
          </p>
          <p className="text-sm text-muted-foreground">
            <strong>Guadalupe:</strong> Multiple rows per doc, NO empty columns, uses Grantor/Grantee roles
          </p>
        </div>

        <Textarea
          value={textData}
          onChange={(e) => setTextData(e.target.value)}
          placeholder={county === 'comal' 
            ? "202506000352\t01/06/2025 01:00:43 PM\tHERNANDEZ ELI GARCIA\tGrantor\tDEED OF TRUST\t\t\tFULGENCIO RAUL\tLot:2 Blk:1 Sub:HEATHER GLEN PH2"
            : "202499001642\t01/24/2024 09:30:14 AM\tTHOMAS CYNTHIA\tGrantee\tDEED OF TRUST\tAc:0.14 Sub:G MALPAZ\tDUQUE SERGIO"
          }
          className="min-h-[300px] font-mono text-sm"
        />

        <div className="flex gap-2">
          <Button 
            onClick={handleProcess} 
            disabled={isProcessing || !textData.trim()}
            className="flex-1"
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing with AI...
              </>
            ) : (
              "Process Records"
            )}
          </Button>

          <Button 
            onClick={handleClearAddresses}
            disabled={isClearing || isProcessing}
            variant="destructive"
          >
            {isClearing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Clearing...
              </>
            ) : (
              "Clear All Addresses"
            )}
          </Button>

          <CleanupFamilyTransfersButton onCleanupComplete={onRecordsSaved} />
        </div>

        {isProcessing && (
          <p className="text-sm text-muted-foreground text-center">
            AI is analyzing {parseComalData(textData).length} records to identify owner-financed deals...
          </p>
        )}
      </CardContent>
    </Card>
  );
}
